import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, Card, Space, Tag, Typography, Table, Divider, Progress, message } from 'antd';
import { monitorAPI, memoAPI } from '../services/api';

const { Text } = Typography;

export default function UrlChecker() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const isAdmin = localStorage.getItem('isAdmin') === '1';
  const [memos, setMemos] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchTimeout, setBatchTimeout] = useState(8000);
  const [completedCount, setCompletedCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [currentBatchTotal, setCurrentBatchTotal] = useState(0);
  const [currentRangeText, setCurrentRangeText] = useState('');
  const [startId, setStartId] = useState('');
  const [endId, setEndId] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [batchController, setBatchController] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detectionLogs, setDetectionLogs] = useState([]);
  const [currentDetecting, setCurrentDetecting] = useState('');

  // id -> { ok, reachable, httpOk, status, durationMs, contentType, finalUrl, error, checkedAt }
  const [latestResults, setLatestResults] = useState({});

  const storageKey = 'urlCheckResults';

  const loadStoredResults = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed ? parsed : {};
    } catch {
      return {};
    }
  };

  const saveStoredResults = (next) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (e) {
      console.warn('Failed to persist urlCheckResults to localStorage:', e);
    }
  };

  useEffect(() => {
    setListLoading(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setListLoading(false);
      message.warning('请先登录后使用批量检测');
      window.location.href = '/login';
      return;
    }
    if (!isAdmin) {
      setListLoading(false);
      message.warning('仅管理员可使用URL检测');
      return;
    }
    memoAPI.getAll({ page: currentPage, limit: pageSize })
      .then((res) => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
        const count = res.data?.pagination?.total || rows.length;
        setMemos(rows);
        setTotal(count);
        const stored = loadStoredResults();
        setLatestResults(stored || {});
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || '加载站点失败';
        message.error(`加载站点失败: ${msg}`);
      })
      .finally(() => setListLoading(false));
  }, [isAdmin, currentPage, pageSize]);

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = String(url).trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `http://${trimmed}`;
  };

  const handleBatchTimeoutChange = (e) => {
    const val = parseInt(e?.target?.value || 0, 10);
    if (!Number.isNaN(val) && val > 0) setBatchTimeout(val);
  };

  const handleStartIdChange = (e) => {
    const val = parseInt(e?.target?.value || '', 10);
    setStartId(Number.isNaN(val) ? '' : val);
  };

  const handleEndIdChange = (e) => {
    const val = parseInt(e?.target?.value || '', 10);
    setEndId(Number.isNaN(val) ? '' : val);
  };

  const handleDetectCurrentPage = () => {
    // 清空手动范围并检测当前页全部
    setStartId('');
    setEndId('');
    setCurrentRangeText('已选择项');
    setTimeout(() => handleBatchCheck(false), 0);
  };

  const handleTableChange = (pagination) => {
    if (pagination?.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
    }
    if (pagination?.current && pagination.current !== currentPage) {
      setCurrentPage(pagination.current);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        width: 70
      },
      {
        title: '站点',
        dataIndex: 'site',
        render: (text) => {
          const url = normalizeUrl(text);
          return (
            <a href={url} target="_blank" rel="noreferrer">{text}</a>
          );
        }
      },
      {
        title: '分组',
        dataIndex: 'group',
        width: 120,
      },
      {
        title: '备注',
        dataIndex: 'remark',
        width: 160,
      },
      {
        title: '最后结果',
        key: 'latest',
        width: 120,
        render: (_, record) => {
          const r = latestResults[record.id];
          if (!r) return <Tag>-</Tag>;
          return (
            <Space size="small">
              <Tag color={r.ok ? 'green' : 'red'}>{r.ok ? '正常' : '异常'}</Tag>
              {typeof r.status === 'number' && <Tag>{r.status}</Tag>}
            </Space>
          );
        }
      },
      {
        title: '响应(ms)',
        key: 'duration',
        width: 110,
        render: (_, record) => {
          const r = latestResults[record.id];
          return r && typeof r.durationMs === 'number' ? <Tag>{r.durationMs}</Tag> : <Tag>-</Tag>;
        }
      },
      {
        title: '检测时间',
        key: 'checkedAt',
        width: 200,
        render: (_, record) => {
          const r = latestResults[record.id];
          return r?.checkedAt ? new Date(r.checkedAt).toLocaleString() : '-';
        }
      },
      {
        title: '错误',
        key: 'error',
        render: (_, record) => {
          const r = latestResults[record.id];
          return r?.error ? <Tag color="red">{r.error}</Tag> : '-';
        }
      }
    ],
    [latestResults]
  );

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const res = await monitorAPI.check({ url: values.url, timeout: values.timeout || 8000 });
      setResult(res.data);
    } catch (e) {
      setResult({ ok: false, reachable: false, httpOk: false, error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const updateOneResult = (id, data) => {
    setLatestResults((prev) => {
      const next = { ...prev, [id]: { ...data, checkedAt: Date.now() } };
      saveStoredResults(next);
      return next;
    });
    
    // 添加检测日志
    const logEntry = {
      id,
      time: new Date().toLocaleTimeString(),
      status: data.ok ? '成功' : '失败',
      duration: data.durationMs || 0,
      statusCode: typeof data.status === 'number' ? data.status : undefined,
      error: data.error || ''
    };
    setDetectionLogs(prev => [...prev, logEntry]);
  };

  const runBatchWithConcurrency = async (items, limit, worker, controller) => {
    return new Promise((resolve) => {
      let idx = 0;
      let active = 0;
      const total = items.length;

      const launchNext = () => {
        if (idx >= total || controller.stopped) {
          if (active === 0) resolve();
          return;
        }
        
        // 暂停检查
        if (controller.paused) {
          setTimeout(launchNext, 100);
          return;
        }

        const current = items[idx++];
        active += 1;
        Promise.resolve()
          .then(() => {
            if (!controller.stopped) {
              return worker(current);
            }
            return undefined;
          })
          .catch(() => {})
          .finally(() => {
            active -= 1;
            if (!controller.stopped) {
              setCompletedCount((x) => x + 1);
            }
            launchNext();
          });

        if (active < limit && !controller.stopped) launchNext();
      };

      const starters = Math.min(limit, total);
      for (let i = 0; i < starters; i += 1) {
        if (!controller.stopped) launchNext();
      }
    });
  };

  const handleBatchCheck = async (onlySelected = true) => {
    console.log('handleBatchCheck called, onlySelected:', onlySelected);
    // 先打开检测过程弹窗
    setModalVisible(true);
    setDetectionLogs([]);
    setCurrentDetecting('准备检测...');
    console.log('Modal should be visible now');
    
    // 确保状态更新
    setTimeout(() => {
      console.log('Modal state after timeout:', modalVisible);
    }, 100);
    
    let list = [];
    if (startId || endId) {
      // 后端分页拉取指定ID范围
      try {
        setListLoading(true);
        const limit = 200;
        // 规范化范围（允许用户把起始和结束写反）
        const rawStart = startId ? parseInt(startId, 10) : undefined;
        const rawEnd = endId ? parseInt(endId, 10) : undefined;
        const normStart = rawStart !== undefined && rawEnd !== undefined && rawStart > rawEnd ? rawEnd : rawStart;
        const normEnd = rawStart !== undefined && rawEnd !== undefined && rawStart > rawEnd ? rawStart : rawEnd;
        setCurrentRangeText(`${normStart ?? '-'} ~ ${normEnd ?? '-'}`);
        let page = 1;
        let totalPages = 1;
        const agg = [];
        do {
          // 使用后端新增的 startId/endId 过滤
          // eslint-disable-next-line no-await-in-loop
          const res = await memoAPI.getAll({ page, limit, startId: normStart, endId: normEnd });
          const rows = Array.isArray(res.data?.data) ? res.data.data : [];
          const pagination = res.data?.pagination;
          totalPages = pagination?.totalPages || 1;
          agg.push(...rows);
          page += 1;
        } while (page <= totalPages);
        list = agg;
      } catch (e) {
        message.error('加载ID范围数据失败');
        setListLoading(false);
        return;
      } finally {
        setListLoading(false);
      }
    } else {
      // 仅选中或当前页
      list = onlySelected ? memos.filter((m) => selectedRowKeys.includes(m.id)) : memos;
      setCurrentRangeText('已选择项');
    }
    
    if (!Array.isArray(list) || list.length === 0) {
      message.info('无可检测站点');
      setModalVisible(false);
      return;
    }
    // 记录本次批量总数用于进度展示
    setCurrentBatchTotal(list.length);
    
    const controller = { paused: false, stopped: false };
    setBatchController(controller);
    setBatchLoading(true);
    setCompletedCount(0);
    setIsPaused(false);
    setIsStopped(false);

    const concurrency = 10;
    const totalCount = list.length;

    await runBatchWithConcurrency(list, concurrency, async (memo) => {
      if (controller.stopped) return;
      
      // 等待暂停状态
      while (controller.paused && !controller.stopped) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (controller.stopped) return;
      
      // 显示当前检测的站点
      setCurrentDetecting(`ID: ${memo.id} - ${memo.site}`);
      
      const url = normalizeUrl(memo.site);
      try {
        const res = await monitorAPI.check({ url, timeout: batchTimeout });
        updateOneResult(memo.id, res.data || {});
      } catch (e) {
        updateOneResult(memo.id, { ok: false, reachable: false, httpOk: false, error: e?.message || '请求失败' });
      }
    }, controller);

    setBatchLoading(false);
    setCompletedCount(totalCount);
    setBatchController(null);
    setCurrentDetecting('');
    
    if (!controller.stopped) {
      message.success('批量检测完成');
    } else {
      message.info('批量检测已停止');
    }
  };

  const handlePause = () => {
    if (batchController) {
      batchController.paused = !batchController.paused;
      setIsPaused(batchController.paused);
      message.info(batchController.paused ? '检测已暂停' : '检测已继续');
    }
  };

  const handleStop = () => {
    if (batchController) {
      batchController.stopped = true;
      // 快速终止：阻止后续任务入队
      setIsStopped(true);
      setBatchLoading(false);
      setCurrentDetecting('');
      message.info('检测已停止');
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setDetectionLogs([]);
    setCurrentDetecting('');
  };

  // 调试弹窗状态
  useEffect(() => {
    console.log('Modal visibility changed:', modalVisible);
  }, [modalVisible]);


  return (
    <Card title="网站可用性检测" border={false}>
      <Form form={form} layout="vertical" onFinish={onFinish} name="urlCheckerForm">
        <Form.Item name="url" label="目标URL" rules={[{ required: true, message: '请输入要检测的URL' }]}>
          <Input id="check-url" name="url" placeholder="https://example.com" autoComplete="url" />
        </Form.Item>
        <Form.Item name="timeout" label="超时时间(毫秒)">
          <Input id="check-timeout" name="timeout" inputMode="numeric" placeholder="8000" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>开始检测</Button>
        </Form.Item>
      </Form>

      {result && (
        <Space direction="vertical" size="small">
          <div>
            <Text>连通性：</Text>
            <Tag color={result.reachable ? 'green' : 'red'}>{result.reachable ? '可达' : '不可达'}</Tag>
          </div>
          <div>
            <Text>HTTP状态：</Text>
            <Tag color={result.httpOk ? 'green' : 'red'}>{result.status || '-'}</Tag>
          </div>
          {typeof result.durationMs === 'number' && (
            <div>
              <Text>响应时间：</Text>
              <Tag>{result.durationMs} ms</Tag>
            </div>
          )}
          {result.contentType && (
            <div>
              <Text>Content-Type：</Text>
              <Tag>{result.contentType}</Tag>
            </div>
          )}
          {result.finalUrl && (
            <div>
              <Text>最终URL：</Text>
              <Tag>{result.finalUrl}</Tag>
            </div>
          )}
          {result.error && (
            <div>
              <Text type="danger">错误：</Text>
              <Tag color="red">{result.error}</Tag>
            </div>
          )}
        </Space>
      )}

      <Divider />

      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <Text strong>批量检测</Text>
          <Input
            style={{ width: 160 }}
            inputMode="numeric"
            id="batch-timeout"
            name="batch-timeout"
            value={batchTimeout}
            onChange={handleBatchTimeoutChange}
            placeholder="超时(毫秒)"
            addonBefore="超时"
            addonAfter="ms"
          />
          <Input
            style={{ width: 120 }}
            inputMode="numeric"
            id="start-id"
            name="start-id"
            value={startId}
            onChange={handleStartIdChange}
            placeholder="起始ID"
            addonBefore="起始"
          />
          <Input
            style={{ width: 120 }}
            inputMode="numeric"
            id="end-id"
            name="end-id"
            value={endId}
            onChange={handleEndIdChange}
            placeholder="结束ID"
            addonBefore="结束"
          />
          <Button onClick={() => setSelectedRowKeys(memos.map((m) => m.id))} disabled={listLoading}>全选</Button>
          <Button onClick={() => setSelectedRowKeys([])} disabled={listLoading}>清空选择</Button>
          <Button type="primary" loading={batchLoading && !isPaused} onClick={() => handleBatchCheck(startId || endId ? false : true)} disabled={batchLoading && !isPaused}>
            {isPaused ? '继续' : '开始'}
          </Button>
          <Button onClick={handlePause} disabled={!batchLoading || isStopped}>
            {isPaused ? '继续' : '暂停'}
          </Button>
          <Button danger onClick={handleStop} disabled={!batchLoading}>
            停止
          </Button>
          <Button loading={batchLoading} onClick={handleDetectCurrentPage} disabled={batchLoading}>检测当前页全部</Button>
          {batchLoading && (
            <Space>
              <Text>进度：</Text>
              <Progress percent={currentBatchTotal ? Math.round((completedCount / Math.max(1, currentBatchTotal)) * 100) : 0} size="small" style={{ width: 160 }} />
              <Text>{completedCount}/{currentBatchTotal}</Text>
              {currentRangeText && (
                <Text type="secondary" style={{ marginLeft: 8 }}>范围：{currentRangeText}</Text>
              )}
            </Space>
          )}
        </Space>

        <Table
          rowKey="id"
          size="small"
          loading={listLoading}
          dataSource={memos}
          columns={columns}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            preserveSelectedRowKeys: true
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `Items ${range[0]}-${range[1]}, total ${total} items`,
            size: 'small'
          }}
          onChange={handleTableChange}
        />
      </Space>

      {modalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: 24
        }}>
          <div style={{
            width: 820,
            maxHeight: '80vh',
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>URL检测过程</Text>
              <Button onClick={handleCloseModal}>关闭</Button>
            </div>
            <div style={{ padding: 16, overflow: 'auto' }}>
              <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
                <Button type="primary" onClick={() => handleBatchCheck(startId || endId ? false : true)} disabled={batchLoading && !isPaused}>
                  {isPaused ? '继续' : (batchLoading ? '进行中' : '开始')}
                </Button>
                <Button onClick={handlePause} disabled={!batchLoading || isStopped}>
                  {isPaused ? '继续' : '暂停'}
                </Button>
                <Button danger onClick={handleStop} disabled={!batchLoading}>停止</Button>
                <Button onClick={handleDetectCurrentPage} disabled={batchLoading}>检测当前页全部</Button>
              </div>
              <div style={{ marginBottom: 16 }}>
                <Text strong>当前检测: </Text>
                <Text type="primary">{currentDetecting || '准备中...'}</Text>
              </div>
              {batchLoading && (
                <div style={{ marginBottom: 16 }}>
                  <Progress 
                    percent={Math.round((completedCount / Math.max(1, currentBatchTotal)) * 100)} 
                    status={isPaused ? 'active' : 'normal'}
                    format={() => `${completedCount}/${currentBatchTotal}`}
                  />
                  {currentRangeText && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">范围：{currentRangeText}</Text>
                    </div>
                  )}
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <Text strong>检测日志:</Text>
              </div>
              <div style={{ height: 300, overflow: 'auto', border: '1px solid #d9d9d9', padding: 8, backgroundColor: '#fafafa' }}>
                {detectionLogs.length === 0 ? (
                  <Text type="secondary">暂无检测记录</Text>
                ) : (
                  detectionLogs.map((log, index) => (
                    <div key={index} style={{ marginBottom: 4, fontSize: 12 }}>
                      <Tag color={log.status === '成功' ? 'green' : 'red'}>{log.status}</Tag>
                      <Text code>ID:{log.id}</Text>
                      <Text type="secondary">{log.time}</Text>
                      {log.duration > 0 && (<Text type="secondary">({log.duration}ms)</Text>)}
                      {typeof log.statusCode === 'number' && (
                        <Text style={{ marginLeft: 8 }}>状态码:{log.statusCode}</Text>
                      )}
                      {log.error && (<Text type="danger"> - {log.error}</Text>)}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


